import React, { useCallback } from 'react';

import { useNarrativeTextInput } from '../../hooks/useNarrativeTextInput';
import { logger } from '../../lib/logger';
import { useCurrentNode, useNarrativeState, useUpdateNode } from '../../store/hooks';
import { Node } from '../../types/narrative';
import { ChoiceTextarea } from '../ChoiceInput';

import { ChoicesList } from './ChoicesList';
import { SectionTitle } from './SectionTitle';

const pathLogger = logger.child('[WorldPathEditor]');

interface WorldPathEditorProps {
  world: 'goodWorld' | 'badWorld';
}

export const WorldPathEditor = React.memo(function WorldPathEditor({
  world,
}: WorldPathEditorProps) {
  const narrativeState = useNarrativeState();
  const currentNode = useCurrentNode();
  const updateNode = useUpdateNode();
  const currentNodeId = narrativeState.currentNodeId;

  const handleTextUpdate = useCallback(
    (value: string) => {
      const field = world === 'goodWorld' ? 'textMainGoodWorld' : 'textMainBadWorld';
      updateNode(currentNodeId, { [field]: value } as Partial<Node>);
    },
    [currentNodeId, updateNode, world]
  );

  const textValue =
    world === 'goodWorld'
      ? currentNode?.textMainGoodWorld || ''
      : currentNode?.textMainBadWorld || '';
  const mainText = useNarrativeTextInput(textValue, handleTextUpdate, 300, 1000);

  const handleChoiceUpdate = useCallback(
    (choiceIndex: number, field: 'text' | 'npcResponse', value: string) => {
      const currentChoices =
        world === 'goodWorld'
          ? currentNode?.choicesGoodWorld || []
          : currentNode?.choicesBadWorld || [];
      const newChoices = [...currentChoices];
      newChoices[choiceIndex] = { ...newChoices[choiceIndex], [field]: value };

      if (world === 'goodWorld') {
        updateNode(currentNodeId, { choicesGoodWorld: newChoices });
      } else {
        updateNode(currentNodeId, { choicesBadWorld: newChoices });
      }
    },
    [currentNode, currentNodeId, updateNode, world]
  );

  // Check if all choices are filled but main text is empty
  const shouldHighlightMainText = React.useMemo(() => {
    if (!currentNode) return false;

    const mainTextEmpty = !mainText.value || mainText.value.trim() === '';
    if (!mainTextEmpty) return false;

    // Get choices for the current world
    const currentChoices =
      world === 'goodWorld' ? currentNode.choicesGoodWorld : currentNode.choicesBadWorld;

    if (!currentChoices || currentChoices.length === 0) return false;

    // Check if all choice fields are filled
    const allChoicesFilled = currentChoices.every((choice) => {
      const hasChoiceText = choice.text && choice.text.trim() !== '';
      return hasChoiceText;
    });

    return allChoicesFilled;
  }, [currentNode, mainText.value, world]);

  pathLogger.debug('Rendered', { world });

  if (!currentNode) {
    return null;
  }

  const containerClass =
    world === 'goodWorld'
      ? 'bg-[#031209] border border-[#059669] rounded-lg p-4'
      : 'bg-[#120303] border border-[#dc2626] rounded-lg p-4';
  const headingClass = world === 'goodWorld' ? 'text-[#10b981]' : 'text-[#f43f5e]';
  const focusClass = world === 'goodWorld' ? 'focus:border-[#059669]' : 'focus:border-[#dc2626]';

  const choices =
    world === 'goodWorld' ? currentNode.choicesGoodWorld : currentNode.choicesBadWorld;

  return (
    <div className={containerClass}>
      <h3 className={`${headingClass} font-semibold mb-4`}>
        {world === 'goodWorld' ? 'Better World Path' : 'Worse World Path'}
      </h3>
      <div className="mb-4">
        <SectionTitle
          title="Text Leading Up to Choices"
          subtitle="After recall"
          text={mainText.value}
          showWordCount={true}
          isWarning={shouldHighlightMainText}
        />
        <ChoiceTextarea
          value={mainText.value}
          onChange={(value) => mainText.onChange(value)}
          placeholder={
            shouldHighlightMainText
              ? '⚠️ Main text is missing - write your text here...'
              : world === 'goodWorld'
                ? 'Good world scenario text...'
                : 'Bad world scenario text...'
          }
          className={`w-full bg-[#161c2e] border rounded-md p-2 text-white focus:outline-none text-sm min-h-[100px] transition-all duration-300 ${
            shouldHighlightMainText
              ? 'border-[#ffcc00] shadow-lg shadow-[#ffcc00]/20 animate-pulse'
              : `border-[#2a3756] ${focusClass}`
          }`}
          maxLength={1000}
        />
      </div>
      <ChoicesList
        choices={choices}
        currentNodeId={currentNodeId}
        onChoiceUpdate={handleChoiceUpdate}
      />
    </div>
  );
});
